import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, X, MapPinOff } from 'lucide-react';
import { importLibrary } from '@googlemaps/js-api-loader';
import { useStreetViewStore } from '@/stores/street-view-store';
import { useMapStore } from '@/stores/map-store';
import { ICON_RAIL_WIDTH } from '@/constants/layout';
import { MANUAL_COVERAGE_RADIUS_M, checkStreetViewCoverage } from '@/map/street-view-coverage';
import { cn } from '@/lib/utils';

/** Zoom the map climbs back to when exiting Street View (above the auto-open dive floor). */
const EXIT_AERIAL_ZOOM = 14;

/** Panorama load progress: resolving imagery, rendering, or no imagery nearby. */
type PanoStatus = 'loading' | 'ready' | 'none';

/**
 * Immersive Street View overlay. Auto-dives arrive with a pre-checked panoId;
 * manual opens (selection card) resolve the nearest panorama here and show a
 * clear empty state when Google has no imagery nearby (e.g. on base). A fresh
 * panorama is constructed per open because the container div unmounts on close.
 */
export function StreetViewPanel(): React.JSX.Element {
  const open = useStreetViewStore((s) => s.open);
  const location = useStreetViewStore((s) => s.location);
  const close = useStreetViewStore((s) => s.close);
  const containerRef = useRef<HTMLDivElement>(null);
  const panoRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const [status, setStatus] = useState<PanoStatus>('loading');

  useEffect(() => {
    if (!open || !location) return;
    let cancelled = false;
    setStatus('loading');
    void resolveAndRender(location, containerRef, panoRef, () => cancelled).then((rendered) => {
      if (!cancelled) setStatus(rendered ? 'ready' : 'none');
    });
    return () => {
      cancelled = true;
      panoRef.current = null; // container unmounts with the overlay — never reuse a dead binding
      setStatus('loading'); // never let a stale 'none' leak into the next open
    };
  }, [open, location]);

  /** Climb back to the aerial view over the same spot, then close. */
  function backToMap(): void {
    if (location && status !== 'none') {
      useMapStore.getState().setFlyTo({ lat: location.lat, lng: location.lon, zoom: EXIT_AERIAL_ZOOM });
    }
    close();
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="absolute z-[1090] flex flex-col overflow-hidden bg-echo-grey"
          style={{ top: 0, right: 0, bottom: 0, left: ICON_RAIL_WIDTH }}
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-echo-border bg-echo-grey/95 px-3 py-2">
            <button
              type="button"
              onClick={backToMap}
              className="flex items-center gap-1.5 rounded-md border border-echo-border px-2.5 py-1.5 font-chakra text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:bg-white/[0.06]"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back to Map
            </button>
            <span className="flex-1 text-center font-chakra text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Street View
            </span>
            <button type="button" onClick={backToMap} aria-label="Exit Street View" className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Container stays mounted regardless of status so an async resolve never lands in a dead ref. */}
          <div className="relative min-h-0 w-full flex-1">
            <div ref={containerRef} className={cn('h-full w-full', status === 'none' && 'invisible')} />
            {status === 'none' && (
              <div className="absolute inset-0 flex bg-echo-grey">
                <NoImagery onBack={backToMap} />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Empty state shown when Google has no panorama near the requested point. */
function NoImagery({ onBack }: { onBack: () => void }): React.JSX.Element {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
      <MapPinOff className="h-8 w-8" />
      <p className="font-chakra text-xs uppercase tracking-wider">
        No Street View imagery within {MANUAL_COVERAGE_RADIUS_M} m of this location
      </p>
      <button
        type="button"
        onClick={onBack}
        className="rounded-md border border-echo-border px-3 py-1.5 font-chakra text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:bg-white/[0.06]"
      >
        Back to Map
      </button>
    </div>
  );
}

/**
 * Resolve a panorama (pinned id from a prior coverage check, else the nearest
 * within the manual radius) and render it into the live container. Returns
 * false when there is no imagery to show.
 */
async function resolveAndRender(
  location: { lat: number; lon: number; heading?: number; panoId?: string },
  containerRef: React.RefObject<HTMLDivElement | null>,
  panoRef: React.MutableRefObject<google.maps.StreetViewPanorama | null>,
  isCancelled: () => boolean,
): Promise<boolean> {
  await importLibrary('streetView');
  const panoId =
    location.panoId ??
    (await checkStreetViewCoverage(location.lat, location.lon, MANUAL_COVERAGE_RADIUS_M))?.panoId;
  if (isCancelled() || !containerRef.current) return false;
  if (!panoId) return false;

  panoRef.current = new google.maps.StreetViewPanorama(containerRef.current, {
    pano: panoId,
    addressControl: false,
    fullscreenControl: false,
    motionTracking: false,
    motionTrackingControl: false,
  });
  panoRef.current.setPov({ heading: location.heading ?? 0, pitch: 0 });
  return true;
}
